import { SUPER_ADMIN } from './roles';

/**
 * The gated admin-panel sections (Settings > Section access).
 *
 * The first four can be opened to Doctors and Admins by a super admin. This is HIDE ONLY: a
 * "SuperAdmin only" section is left out of a Doctor's or Admin's menu and its URL sends them back to
 * the Appointments page. Only Statistics is also refused by the API; Clinicians, Activation codes and
 * Vouchers keep the server access they always had.
 *
 * Staff activity is different: it is ALWAYS super admins only (superAdminOnly below). Section access
 * lists it as a locked row that cannot be changed, the menu and its URL are only ever open to a super
 * admin, and its API refuses everyone else (403).
 */
export const SECTIONS = {
    CLINICIANS: 'clinicians',
    ACTIVATION_CODES: 'activationCodes',
    VOUCHERS: 'vouchers',
    STATISTICS: 'statistics',
    STAFF_ACTIVITY: 'staffActivity',
};

/**
 * Who may see a section. 'all' = Doctors and Admins (super admins always see every section).
 * Anything the server stores other than exactly 'all' means superAdmin.
 */
export const SECTION_AUDIENCE = {
    ALL: 'all',
    SUPER_ADMIN: 'superAdmin',
};

/**
 * In the order the server lists them (and the Settings card shows them). `superAdminOnly`: never
 * visible to a Doctor or Admin, whatever any answer says, and never changeable in Section access.
 */
export const SECTION_LIST = [
    { key: SECTIONS.CLINICIANS, label: 'Clinicians management', path: '/clinicians-management' },
    { key: SECTIONS.ACTIVATION_CODES, label: 'Activation codes', path: '/activation-code' },
    { key: SECTIONS.VOUCHERS, label: 'Vouchers', path: '/voucher' },
    { key: SECTIONS.STATISTICS, label: 'Statistics', path: '/statistics' },
    { key: SECTIONS.STAFF_ACTIVITY, label: 'Staff activity', path: '/staff-activity', superAdminOnly: true },
];

export const SECTION_LOCKED_MESSAGE = 'That section is limited to super admins.';

/**
 * The notice when a Doctor or Admin is sent back from a gated section although no check has ever
 * succeeded for them: the stored-role fallback shows nothing, so it may not be locked at all.
 */
export const SECTION_CHECK_FAILED_MESSAGE = 'Could not check your access to that section. Please try again.';

/** Every section set to one value. */
export function everySection(visible) {
    return SECTION_LIST.reduce((sections, { key }) => ({ ...sections, [key]: visible }), {});
}

/**
 * What to show before the server has answered, or when it could not: a super admin sees
 * everything (the server would say so too), anyone else sees none of the gated sections.
 * Fails closed.
 */
export function fallbackSections(role) {
    return everySection(role === SUPER_ADMIN);
}

/**
 * The server's answer for a Doctor or Admin (a super admin sees every section), with anything missing
 * or not strictly true read as hidden - and a super-admin-only section always hidden, even if an
 * answer said otherwise.
 */
export function normaliseSections(sections) {
    return SECTION_LIST.reduce(
        (result, { key, superAdminOnly }) => ({
            ...result,
            [key]: !superAdminOnly && Boolean(sections) && sections[key] === true,
        }),
        {},
    );
}
